import Papa from "papaparse";

export type CSVMetadataValid = {
  valid: true;
  rows: number;
  columns: string[];
  sampleRows: { [key: string]: string }[];
};

export type CSVMetadataInvalid = {
  valid: false;
  errors: string[];
};

export type CSVMetadata = CSVMetadataValid | CSVMetadataInvalid;

const MAX_SAMPLE_ROWS = 5;

export async function parseCsv(
  file: File,
  onProgress?: (rowsLoaded: number) => void
): Promise<CSVMetadata> {
  let rowsLoaded = 0;
  const errors: string[] = [];
  let additionalErrorCount = 0;
  let meta: Papa.ParseMeta;
  let sampleRows: { [key: string]: string }[] = [];

  return new Promise(resolve => {
    Papa.parse(file, {
      delimiter: ",",
      worker: true,
      header: true,
      skipEmptyLines: true,
      chunk: function (results) {
        meta = results.meta;
        rowsLoaded += results.data.length;
        if (sampleRows.length < MAX_SAMPLE_ROWS) {
          sampleRows = [
            ...sampleRows,
            ...(results.data.slice(0, MAX_SAMPLE_ROWS - sampleRows.length) as {
              [key: string]: string;
            }[]),
          ];
        }

        results.errors.forEach(e => {
          if (errors.length < 10) {
            console.error(e);

            // only a few errors so far, add this one to the list
            let message = e.message;
            if (e.row) {
              message += ` (row ${e.row})`;
            }

            errors.push(message);
          } else {
            // lots of errors; just count them
            additionalErrorCount++;
          }
        });

        onProgress?.(rowsLoaded);
      },
      complete: function () {
        if (errors.length > 0) {
          if (additionalErrorCount > 0) {
            errors.push(
              `...and ${additionalErrorCount} more ${
                additionalErrorCount === 1 ? "error" : "errors"
              }`
            );
          }

          resolve({
            valid: false,
            errors,
          });
          return;
        }

        resolve({
          valid: true,
          rows: rowsLoaded,
          columns: meta.fields!,
          sampleRows,
        });
      },
      error: function (error) {
        console.error(error);

        resolve({
          valid: false,
          errors: [`Unable to read CSV file: ${error.message}`],
        });
      },
    });
  });
}

export const PHONE_NUMBER_DEFAULT_COLUMN = "phonenumber";

function normalizeColumnName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function matchColumnName(
  metadata: { columns: string[] },
  normalizedNames: string | string[]
): string | undefined {
  return metadata.columns.find(col =>
    Array.isArray(normalizedNames)
      ? normalizedNames.includes(normalizeColumnName(col))
      : normalizeColumnName(col) === normalizedNames
  );
}
